import React from "react";

import { Agent } from "../types/Agent";
import {
  EnvelopeIcon,
  FacebookIcon,
  LineIcon,
  LinkedInIcon,
  PhoneIcon,
  TwitterIcon,
  UserIcon,
  WechatIcon,
  WeiboIcon,
  WhatsappIcon,
} from "../icons";
import classNames from "classnames";
import { GlobeIcon } from "../icons/GlobeIcon";
import { isValidURL, makeAbsoluteURL } from "../services/Utils";

function SocialLink({
  href,
  title,
  className = "w-5",
  icon,
}: {
  href?: string;
  title: string;
  className?: string;
  icon: React.ReactNode;
}) {
  return (
    href &&
    isValidURL(href) && (
      <a
        href={href}
        target="_blank"
        title={title}
        className={classNames(className, "text-gray-500 hover:text-gray-700")}
      >
        {icon}
      </a>
    )
  );
}

function AgentLink({
  href,
  target,
  label,
  title,
  icon,
}: {
  href?: string;
  target?: string;
  label?: string;
  title: string;
  icon: React.ReactNode;
}) {
  return (
    href && (
      <a
        href={href}
        target={target}
        className="flex gap-2 items-center text-brand-primary"
        title={title}
      >
        <div className="w-4">{icon}</div>
        <span>{label}</span>
      </a>
    )
  );
}

function AgentCard({ agent }: { agent: Agent }) {
  return (
    <li className="overflow-hidden rounded-md border border-gray-200 bg-white">
      <div className="flex flex-col sm:flex-row sm:items-center gap-y-5 gap-x-4 border-gray-900/5 p-6">
        <div className="self-center">
          {agent.imageUrl ? (
            <img
              src={agent.imageUrl}
              alt={agent.name}
              className="h-16 flex-none bg-white object-cover ring-1 ring-gray-900/10"
            />
          ) : (
            <div className="h-16 w-16 flex-none bg-gray-200 ring-1 ring-gray-900/10 rounded-full px-5 pt-[1.1rem]">
              <UserIcon />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <div className="font-medium leading-6 text-gray-900 ">
            {agent.name}
          </div>
          {agent.countryCode && (
            <div className="flex items-center gap-2">
              <img
                src={agent.flagUrl}
                title={agent.countryName}
                alt={`Flag of ${agent.countryName}`}
                className="h-5 w-5"
              />
              <div className="font-medium leading-6 text-gray-600">
                {agent.countryName}
              </div>
            </div>
          )}
        </div>
      </div>

      {agent.address && (
        <div
          className="text-sm text-gray-600 px-6 pb-6"
          dangerouslySetInnerHTML={{ __html: agent.address }}
        />
      )}

      <div className="-mt-px flex flex-col sm:flex-row gap-5 sm:gap-3 items-start sm:items-center justify-between border-t p-6 text-gray-600">
        <div className="flex flex-col gap-2 text-sm empty:hidden">
          <AgentLink
            href={makeAbsoluteURL(agent.website)}
            target="_blank"
            label={agent.website}
            title="Website"
            icon={<GlobeIcon />}
          />
          <AgentLink
            href={agent.email && `mailto:${agent.email}`}
            label={agent.email}
            title="Email"
            icon={<EnvelopeIcon />}
          />
          <AgentLink
            href={agent.phone && `tel:${agent.phone}`}
            label={agent.phone}
            title="Phone"
            icon={<PhoneIcon />}
          />
        </div>

        <div className="flex flex-wrap gap-5 items-center justify-center sm:justify-start empty:hidden">
          <SocialLink
            href={agent.socialLinks.line}
            title="Line"
            icon={<LineIcon />}
          />
          <SocialLink
            href={agent.socialLinks.linkedin}
            title="LinkedIn"
            icon={<LinkedInIcon />}
          />
          <SocialLink
            href={agent.socialLinks.facebook}
            title="Facebook"
            className="w-7"
            icon={<FacebookIcon />}
          />
          <SocialLink
            href={agent.socialLinks.twitter}
            title="X (formerly Twitter)"
            icon={<TwitterIcon />}
          />
          <SocialLink
            href={agent.socialLinks.wechat}
            title="Wechat"
            className="w-7"
            icon={<WechatIcon />}
          />
          <SocialLink
            href={agent.socialLinks.whatsapp}
            title="Whatsapp"
            icon={<WhatsappIcon />}
          />
          <SocialLink
            href={agent.socialLinks.weibo}
            title="Weibo"
            className="w-7"
            icon={<WeiboIcon />}
          />
        </div>
      </div>
    </li>
  );
}

export { AgentCard };
